import React, { useContext, useEffect, useMemo, useState } from "react";
import MapClickEventComponent from "components/common/MapClickEventComponent";
import MapLayersComponent from "components/common/MapLayersComponent";
import LocaleContext from "constants/localization";
import { isEmpty } from "lodash";
import { finCRS } from "../../map/finCRS";
import API from "constants/api";
import SaveButton from "components/common/SaveButton";
import { resToJsonHandleErrors } from "helpers/actionHelpers";
import Loading from "components/common/Loading";

import "proj4";
import "proj4leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer } from "react-leaflet";

import MapSearchResultComponent from "./MapSearchResultComponent";

export default function MapSearchView({
  constraint,
  searchView,
  showPreviousSearchResults,
}) {
  const [map, setMap] = useState(null);
  const [display, setDisplay] = useState(false);
  const [mapCenter, setMapCenter] = useState([
    61.777460617359054, 24.7476951251703,
  ]);
  const [zoom, setZoom] = useState(0);
  const [wfsEnabled, setWfsEnabled] = useState(false);
  const [getFeatureInfoEnabled, setGetFeatureInfoEnabled] = useState(false);
  const [searchButtonDisabled, setSearchButtonDisabled] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [layerConfig, setLayerConfig] = useState({});
  const LS = useContext(LocaleContext);

  const PREVIOUS_SEARCH_RESULTS = "previousSearchResults";
  const PREVIOUS_CENTER = "previousCenter";
  const PREVIOUS_ZOOM = "previousZoom";

  showPreviousSearchResults = showPreviousSearchResults === "true";

  useEffect(() => {
    console.log("showPreviousSearchResults", showPreviousSearchResults);
    if (showPreviousSearchResults == true) {
      const previousSearchResults = window.sessionStorage.getItem(
        PREVIOUS_SEARCH_RESULTS
      );
      if (previousSearchResults) {
        setSearchResults(JSON.parse(previousSearchResults));
      }
    } else {
      window.sessionStorage.setItem(PREVIOUS_SEARCH_RESULTS, null);
    }

    fetch(API.MAP_CONFIG)
      .then((res) => res.json())
      .then((res) => {
        if (!isEmpty(res)) {
          if (!isEmpty(res.center)) {
            setMapCenter([res.center.x, res.center.y]);
            setZoom(res.center.zoom);
          }
          setWfsEnabled(res.wfs.enabled);
          setGetFeatureInfoEnabled(res.getFeatureInfo.enabled);
          if (!isEmpty(res.layers)) {
            setLayerConfig(res.layers);
          }
          setDisplay(true);
        }
        if (showPreviousSearchResults == true) {
          const previousCenter = window.sessionStorage.getItem(PREVIOUS_CENTER);
          const previousZoom = Number(
            window.sessionStorage.getItem(PREVIOUS_ZOOM)
          );
          if (previousCenter && previousZoom) {
            setMapCenter(previousCenter.split(",").map(Number));
            setZoom(Number(previousZoom));
          }
        }
      })
      .catch((e) => {
        if (!(e instanceof SyntaxError)) {
          throw e;
        }
      });
  }, []);

  useEffect(() => {
    if (map) {
      map.setView(mapCenter, zoom, { animate: false });
    }
  }, [mapCenter, zoom]);

  const submitSearch = () => {
    const formData = new FormData();
    formData.set("style", "map");
    formData.set("searchArea", boundsToSparql(map.getBounds()));
    formData.set("constraint", constraint);
    formData.set("searchView", searchView);

    setSearchButtonDisabled(true);
    fetch("mapSearch.do", {
      method: "POST",
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((res) => {
        setSearchResults(res);
        console.log("storing search results in session", JSON.stringify(res));
        window.sessionStorage.setItem(
          PREVIOUS_SEARCH_RESULTS,
          JSON.stringify(res)
        );
      })
      .then(() => setSearchButtonDisabled(false));
  };

  /**
   * Converts a Leaflet bounds object into a string of points which can be
   * directly entered into a SPARQL query. This means the first and last point in
   * the string are equal.
   * @param {*} b bounds object
   * @returns SPARQL compatible string of points
   */
  const boundsToSparql = (b) => {
    const string =
      "" +
      b.getNorth() +
      " " +
      b.getEast() +
      ", " +
      b.getSouth() +
      " " +
      b.getEast() +
      ", " +
      b.getSouth() +
      " " +
      b.getWest() +
      ", " +
      b.getNorth() +
      " " +
      b.getWest() +
      ", " +
      b.getNorth() +
      " " +
      b.getEast();
    return string;
  };

  const layersComponent = useMemo(
    () => (
      <MapLayersComponent layerConfig={layerConfig} wfsEnabled={wfsEnabled} />
    ),
    [layerConfig, wfsEnabled]
  );

  const displayMap = useMemo(
    () => (
      <MapContainer
        center={mapCenter}
        style={{ height: "75vh", width: "100%", cursor: "crosshair" }}
        zoom={zoom}
        crs={finCRS}
        scrollWheelZoom={true}
        maxBounds={[
          [57.15273773771963, 50.28861573758486],
          [73.61064411453262, 10.730096654154679],
        ]}
        whenCreated={(map) => setMap(map)}
      >
        {layersComponent}
        <MapClickEventComponent
          searchView={true}
          getFeatureInfoEnabled={getFeatureInfoEnabled}
          overlayMapDetails={layerConfig.overlayMapDetails}
        />
        <MapSearchResultComponent searchResults={searchResults} />
      </MapContainer>
    ),
    [mapCenter, zoom, getFeatureInfoEnabled, searchResults, layerConfig]
  );

  // Store map position in session to restore when returning to map search view
  map?.on("moveend", () => {
    const c = map.getCenter();
    window.sessionStorage.setItem(PREVIOUS_CENTER, [c.lat, c.lng]);
    window.sessionStorage.setItem(PREVIOUS_ZOOM, map.getZoom());
  });

  if (display) {
    return (
      <div>
        {displayMap}
        <SaveButton
          label="Häe täältä alueelta"
          disabled={searchButtonDisabled}
          onSubmit={() => submitSearch()}
        />
      </div>
    );
  } else {
    return <Loading />;
  }
}
